import { z } from 'zod';
import { identitySessionSchema, memberSchema } from '../domain/identity';
import {
  CLOUD_SCHEMA_VERSION,
  cloudPinChangeSchema,
  cloudSchoolDataSchema,
  type CloudConfigSnapshot,
  type CloudErrorCode,
  type CloudErrorResponse,
  type CloudHealthResponse,
  type CloudLoginResponse,
  type CloudSessionResponse,
} from '../domain/cloud';
import type { BackupData } from '../domain/types';

const CLOUD_REQUEST_TIMEOUT_MS = 12_000;

const configSnapshotSchema: z.ZodType<CloudConfigSnapshot> = z.object({
  data: cloudSchoolDataSchema,
  revision: z.number().int().min(1),
  updatedAt: z.string().datetime({ offset: true }),
  schemaVersion: z.literal(CLOUD_SCHEMA_VERSION),
});

const healthResponseSchema: z.ZodType<CloudHealthResponse> = z.object({
  ok: z.literal(true),
  service: z.literal('waschpilot-cloud'),
  schemaVersion: z.literal(CLOUD_SCHEMA_VERSION),
  configured: z.boolean(),
  teacherPinConfigured: z.boolean(),
  database: z.literal(true),
  migrationVersion: z.number().int().nonnegative().optional(),
  timestamp: z.string().datetime({ offset: true }),
});

const loginResponseSchema: z.ZodType<CloudLoginResponse> = z.object({
  authenticated: z.literal(true),
  user: memberSchema.optional(),
  csrfToken: z.string().min(16).max(500),
  expiresAt: z.string().datetime({ offset: true }),
});

const sessionResponseSchema: z.ZodType<CloudSessionResponse> = z.union([
  loginResponseSchema,
  z.object({ authenticated: z.literal(false) }),
]);

const errorResponseSchema: z.ZodType<CloudErrorResponse> = z.object({
  error: z.object({
    code: z.enum([
      'forbidden',
      'invalid_link',
      'account_exists',
      'bad_request',
      'body_too_large',
      'csrf_failed',
      'database_unavailable',
      'forbidden_origin',
      'invalid_credentials',
      'not_authenticated',
      'not_configured',
      'rate_limited',
      'revision_conflict',
      'server_misconfigured',
      'stored_config_invalid',
    ]),
    message: z.string(),
  }),
  current: configSnapshotSchema.optional(),
});

export type CloudClientErrorCode = CloudErrorCode | 'invalid_response' | 'network';

export class CloudApiError extends Error {
  constructor(
    public readonly code: CloudClientErrorCode,
    public readonly status: number,
    message: string,
    public readonly current?: CloudConfigSnapshot,
  ) {
    super(message);
    this.name = 'CloudApiError';
  }
}

function requestHeaders(jsonBody: boolean, csrfToken?: string): Headers {
  const headers = new Headers({ Accept: 'application/json' });
  if (jsonBody) headers.set('Content-Type', 'application/json');
  if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
  return headers;
}

async function decodeJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new CloudApiError('invalid_response', response.status, 'The server response is invalid.');
  }
}

export async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const controller = new AbortController();
  const callerSignal = init.signal;
  const abortFromCaller = () => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) abortFromCaller();
  else callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
  const timeout = window.setTimeout(() => controller.abort(), CLOUD_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(path, {
      ...init,
      cache: 'no-store',
      credentials: 'same-origin',
      redirect: 'error',
      signal: controller.signal,
    });
    const body = await decodeJson(response);
    if (!response.ok) {
      const parsed = errorResponseSchema.safeParse(body);
      if (parsed.success) {
        throw new CloudApiError(
          parsed.data.error.code,
          response.status,
          parsed.data.error.message,
          parsed.data.current,
        );
      }
      throw new CloudApiError(
        'invalid_response',
        response.status,
        'The server response is invalid.',
      );
    }
    return body;
  } catch (error) {
    if (controller.signal.aborted) {
      throw new CloudApiError('network', 0, 'The cloud service is unavailable.');
    }
    if (error instanceof CloudApiError) throw error;
    throw new CloudApiError('network', 0, 'The cloud service is unavailable.');
  } finally {
    window.clearTimeout(timeout);
    callerSignal?.removeEventListener('abort', abortFromCaller);
  }
}

function parseResponse<T>(schema: z.ZodType<T>, body: unknown): T {
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new CloudApiError('invalid_response', 200, 'The server response is invalid.');
  }
  return parsed.data;
}

export class CloudApiClient {
  private manuallyLocked = false;
  constructor(public readonly accounts = true) {}
  private isLocked(): boolean {
    if (!this.accounts) return false;
    try {
      return this.manuallyLocked || localStorage.getItem('waschpilot-teacher-locked') === '1';
    } catch {
      return this.manuallyLocked;
    }
  }
  private rememberLock(locked: boolean): void {
    this.manuallyLocked = locked;
    try {
      if (locked) localStorage.setItem('waschpilot-teacher-locked', '1');
      else localStorage.removeItem('waschpilot-teacher-locked');
    } catch {
      /* Storage may be disabled; keep the in-memory lock. */
    }
  }
  async loginAccount(username: string, password: string): Promise<void> {
    const session = identitySessionSchema.parse(
      await request('/api/identity/login', {
        method: 'POST',
        headers: requestHeaders(true),
        body: JSON.stringify({ username, password }),
      }),
    );
    this.csrfToken = session.csrfToken;
    this.expiresAt = session.expiresAt;
    this.rememberLock(false);
  }
  async identityRequest(path: string, body?: unknown): Promise<unknown> {
    if (body !== undefined && !this.csrfToken) await this.requireSession();
    return request(
      '/api/identity/' + path,
      body === undefined
        ? {}
        : {
            method: 'POST',
            headers: requestHeaders(true, this.csrfToken ?? undefined),
            body: JSON.stringify(body),
          },
    );
  }
  private csrfToken: string | null = null;
  private expiresAt: string | null = null;

  async health(): Promise<CloudHealthResponse> {
    return parseResponse(healthResponseSchema, await request('/api/health'));
  }

  async getConfig(): Promise<CloudConfigSnapshot> {
    return parseResponse(configSnapshotSchema, await request('/api/config'));
  }

  async login(pin: string): Promise<CloudLoginResponse> {
    const session = parseResponse(
      loginResponseSchema,
      await request('/api/auth/login', {
        method: 'POST',
        headers: requestHeaders(true),
        body: JSON.stringify({ pin }),
      }),
    );
    this.csrfToken = session.csrfToken;
    this.expiresAt = session.expiresAt;
    return session;
  }

  async session(): Promise<CloudSessionResponse> {
    const session = parseResponse(
      sessionResponseSchema,
      await request(this.accounts ? '/api/identity/session' : '/api/auth/session'),
    );
    if (this.isLocked()) {
      // Offline logout must not silently restore access on reload. Revoke when reachable.
      if (session.authenticated) {
        await request('/api/identity/logout', {
          method: 'POST',
          headers: requestHeaders(false, session.csrfToken),
        });
      }
      this.csrfToken = null;
      this.expiresAt = null;
      return { authenticated: false };
    }
    this.csrfToken = session.authenticated ? session.csrfToken : null;
    this.expiresAt = session.authenticated ? session.expiresAt : null;
    return session;
  }

  async updateConfig(expectedRevision: number, data: BackupData): Promise<CloudConfigSnapshot> {
    if (this.isLocked()) throw new CloudApiError('not_authenticated', 401, 'Sign in again.');
    if (!this.csrfToken) await this.requireSession();
    return parseResponse(
      configSnapshotSchema,
      await request('/api/config', {
        method: 'PUT',
        headers: requestHeaders(true, this.csrfToken ?? undefined),
        body: JSON.stringify({ expectedRevision, data }),
      }),
    );
  }

  async changePin(currentPin: string, newPin: string): Promise<{ changed: true }> {
    if (!this.csrfToken) await this.requireSession();
    const body = cloudPinChangeSchema.parse({ currentPin, newPin });
    return parseResponse(
      z.object({ changed: z.literal(true) }),
      await request('/api/auth/change-pin', {
        method: 'POST',
        headers: requestHeaders(true, this.csrfToken ?? undefined),
        body: JSON.stringify(body),
      }),
    );
  }

  async logout(): Promise<void> {
    if (this.accounts) this.rememberLock(true);
    try {
      if (this.csrfToken) {
        await request(this.accounts ? '/api/identity/logout' : '/api/auth/logout', {
          method: 'POST',
          headers: requestHeaders(false, this.csrfToken),
        });
      }
    } finally {
      this.csrfToken = null;
      this.expiresAt = null;
    }
  }

  clearSession(): void {
    this.csrfToken = null;
    this.expiresAt = null;
  }

  get sessionExpiresAt(): string | null {
    return this.expiresAt;
  }

  private async requireSession(): Promise<void> {
    const session = await this.session();
    if (!session.authenticated) {
      throw new CloudApiError('not_authenticated', 401, 'Teacher authentication is required.');
    }
  }
}

export const cloudApi = new CloudApiClient(import.meta.env.VITE_TEACHER_AUTH_MODE !== 'legacy');
